import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { useQueryLog } from './useQueryLog'

export function usePolicies(page = 1, pageSize = 100, policySearch = '', clientSearch = '', sortBy = 'created_at', sortOrder = 'desc', advisorId = null, providerSearch = '', productSearch = '') {
  const { logQuery } = useQueryLog()
  const [policies, setPolicies] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchPolicies()
  }, [page, policySearch, clientSearch, sortBy, sortOrder, advisorId, providerSearch, productSearch])

  const fetchPolicies = async () => {
    try {
      setLoading(true)
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1

      // When filtering by advisor, first get their policy IDs
      let policyIds = null
      if (advisorId) {
        logQuery('SELECT', 'policy_advisors')
        const { data: pa } = await supabase
          .from('policy_advisors')
          .select('policy_id')
          .eq('advisor_id', advisorId)
        policyIds = (pa || []).map(r => r.policy_id)
        if (policyIds.length === 0) {
          setPolicies([])
          setTotal(0)
          setLoading(false)
          return
        }
      }

      logQuery('SELECT', 'policies')
      let query = supabase
        .from('policies')
        .select(
          '*, product_providers(id, name), entities(id, name), clients(id, full_name), policy_attachments(id, file_name, file_url), policy_advisors(id, advisor_id, split_percentage, advisors(id, full_name))',
          { count: 'exact' }
        )

      if (policyIds) {
        query = query.in('id', policyIds)
      }

      // Policy number search (direct filter on base table)
      if (policySearch) {
        query = query.ilike('policy_number', `%${policySearch}%`)
      }

      // Client name search — two-step: resolve client IDs first
      if (clientSearch) {
        logQuery('SELECT', 'clients')
        const { data: matchedClients } = await supabase
          .from('clients')
          .select('id')
          .ilike('full_name', `%${clientSearch}%`)
        const clientIds = (matchedClients || []).map(c => c.id)
        if (clientIds.length === 0) {
          // No matching clients — return empty results immediately
          setPolicies([])
          setTotal(0)
          setLoading(false)
          return
        }
        query = query.in('client_id', clientIds)
      }

      // Provider name search — two-step: resolve provider IDs first
      if (providerSearch) {
        logQuery('SELECT', 'product_providers')
        const { data: matchedProviders } = await supabase
          .from('product_providers')
          .select('id')
          .ilike('name', `%${providerSearch}%`)
        const providerIds = (matchedProviders || []).map(p => p.id)
        if (providerIds.length === 0) {
          // No matching providers — return empty results immediately
          setPolicies([])
          setTotal(0)
          setLoading(false)
          return
        }
        query = query.in('product_provider_id', providerIds)
      }

      // Product name search
      if (productSearch) {
        query = query.ilike('product', `%${productSearch}%`)
      }

      // Handle joined-table sort keys
      if (sortBy === 'client') {
        query = query.order('full_name', { referencedTable: 'clients', ascending: sortOrder === 'asc' })
      } else if (sortBy === 'product_provider') {
        query = query.order('name', { referencedTable: 'product_providers', ascending: sortOrder === 'asc' })
      } else {
        query = query.order(sortBy, { ascending: sortOrder === 'asc' })
      }

      const { data, count, error: err } = await query.range(from, to)

      if (err) throw err

      // Advisor names are already included in the query via policy_advisors(*advisors)
      // No need for separate lookup — data already has advisor info
      setPolicies(data || [])
      setTotal(count || 0)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const createPolicy = async (policyData) => {
    logQuery('INSERT', 'policies')
    const { data, error } = await supabase
      .from('policies')
      .insert([policyData])
      .select()
    if (error) throw error
    await fetchPolicies()
    return data[0]
  }

  const updatePolicy = async (id, policyData) => {
    const { data, error } = await supabase
      .from('policies')
      .update(policyData)
      .eq('id', id)
      .select()
    if (error) throw error
    await fetchPolicies()
    return data[0]
  }

  const toggleActive = async (id, currentValue) => {
    setPolicies(prev => prev.map(p => p.id === id ? { ...p, active: !currentValue } : p))
    const { error } = await supabase
      .from('policies')
      .update({ active: !currentValue })
      .eq('id', id)
    if (error) {
      setPolicies(prev => prev.map(p => p.id === id ? { ...p, active: currentValue } : p))
      console.error('toggleActive error:', error)
    }
  }

  // Returns { imported: number, skipped: string[] }
  const importPolicies = async (data, onProgress = null) => {
    logQuery('INSERT', 'policies')

    // Deduplicate within the batch by policy_number, merging _advisorSplits across rows
    // (handles Excel files where one row = one advisor per policy)
    const seenInBatch = new Map()
    data.forEach(r => {
      const key = String(r.policy_number)
      if (seenInBatch.has(key)) {
        const prev = seenInBatch.get(key)
        const merged = [...(prev._advisorSplits || []), ...(r._advisorSplits || [])]
        seenInBatch.set(key, { ...prev, _advisorSplits: merged.length ? merged : undefined })
      } else {
        seenInBatch.set(key, r)
      }
    })
    const uniqueData = [...seenInBatch.values()]
    const policyNumbers = uniqueData.map(r => String(r.policy_number)).filter(Boolean)
    const totalPolicies = uniqueData.length

    // 1. Fetch existing rows once so we can avoid rewriting unchanged policies
    const { data: existing } = await supabase
      .from('policies')
      .select('policy_number, product_provider_id, product_provider, category, product, customer_code, client_id, entity_id, "group", notes, active, start_date, end_date')
      .in('policy_number', policyNumbers)
    const existingByNumber = new Map((existing || []).map(r => [String(r.policy_number), r]))

    // 2. Resolve customer_code → client_id
    const codes = [...new Set(uniqueData.map(r => r.customer_code).filter(Boolean))]
    let codeToClientId = {}
    if (codes.length > 0) {
      const { data: clients } = await supabase
        .from('clients')
        .select('id, customer_code')
        .in('customer_code', codes)
      if (clients) {
        clients.forEach(c => { codeToClientId[c.customer_code] = c.id })
      }
    }

    // 3. Resolve entity names → entity_id, auto-creating stubs for unknowns
    const entityNames = [...new Set(uniqueData.map(r => r.entity).filter(Boolean))]
    let nameToEntityId = {}
    if (entityNames.length > 0) {
      const { data: existingEnt } = await supabase
        .from('entities')
        .select('id, name')
        .in('name', entityNames)
      ;(existingEnt || []).forEach(e => { nameToEntityId[e.name] = e.id })
      const missing = entityNames.filter(n => !nameToEntityId[n])
      if (missing.length > 0) {
        const { data: created } = await supabase
          .from('entities')
          .insert(missing.map(n => ({ name: n, active: true })))
          .select('id, name')
        ;(created || []).forEach(e => { nameToEntityId[e.name] = e.id })
      }
    }

    // Only upsert columns that actually exist in the policies table
    const SAFE_COLUMNS = [
      'policy_number', 'product_provider_id', 'product_provider',
      'category', 'product', 'customer_code', 'client_id', 'entity_id',
      'group', 'notes', 'active', 'start_date', 'end_date',
    ]

    const enriched = uniqueData.map(row => {
      const out = {}
      SAFE_COLUMNS.forEach(col => { if (row[col] !== undefined) out[col] = row[col] })
      if (row.customer_code && codeToClientId[row.customer_code] && !out.client_id) {
        out.client_id = codeToClientId[row.customer_code]
      }
      if (row.entity && nameToEntityId[row.entity] && !out.entity_id) {
        out.entity_id = nameToEntityId[row.entity]
      }
      return out
    })

    const normalize = (value) => {
      if (value === undefined) return undefined
      if (value === null) return null
      if (typeof value === 'string') return value.trim()
      if (typeof value === 'boolean') return value
      return String(value)
    }

    const hasChanges = (incoming, existingRow) => {
      if (!existingRow) return true
      return SAFE_COLUMNS.some(col => normalize(incoming[col]) !== normalize(existingRow[col]))
    }

    const toUpsert = enriched.filter(row => hasChanges(row, existingByNumber.get(String(row.policy_number))))
    const newCount = enriched.filter(r => !existingByNumber.has(String(r.policy_number))).length
    const updatedCount = toUpsert.length - newCount

    if (toUpsert.length > 0) {
      // Batch upsert to avoid statement timeouts on large imports
      const BATCH_SIZE = 15
      for (let i = 0; i < toUpsert.length; i += BATCH_SIZE) {
        const batch = toUpsert.slice(i, i + BATCH_SIZE)
        const { error } = await supabase
          .from('policies')
          .upsert(batch, { onConflict: 'policy_number' })
        if (error) throw error

        // Report progress: how many policies processed so far
        const processedCount = Math.min(i + BATCH_SIZE, toUpsert.length)
        if (onProgress) onProgress({ current: processedCount, total: totalPolicies, phase: 'Upserting policies' })
      }
    }

    // Process advisor splits separately (after policies exist)
    // Build policy_number → policy_id map for the upserted rows
    if (uniqueData.some(r => r._advisorSplits?.length)) {
      const { data: policyMap } = await supabase
        .from('policies')
        .select('id, policy_number')
        .in('policy_number', uniqueData.filter(r => r._advisorSplits?.length).map(r => String(r.policy_number)))

      const numberToId = new Map((policyMap || []).map(p => [String(p.policy_number), p.id]))

      // Collect all advisor names from splits
      const allAdvisorNames = new Set()
      uniqueData.forEach(r => {
        r._advisorSplits?.forEach(s => allAdvisorNames.add(String(s.name).trim()))
      })

      // Resolve advisor names to IDs
      let nameToAdvisorId = {}
      if (allAdvisorNames.size > 0) {
        const { data: advisors } = await supabase
          .from('advisors')
          .select('id, full_name')
          .in('full_name', [...allAdvisorNames])
        ;(advisors || []).forEach(a => { nameToAdvisorId[a.full_name] = a.id })
      }

      // Build policy_advisors records
      const policyAdvisorRecords = []
      uniqueData.forEach(row => {
        const policyId = numberToId.get(String(row.policy_number))
        if (!policyId) return

        row._advisorSplits?.forEach(split => {
          const advisorId = nameToAdvisorId[String(split.name).trim()]
          if (!advisorId) return // Skip unmatched advisors

          policyAdvisorRecords.push({
            policy_id: policyId,
            advisor_id: advisorId,
            split_percentage: split.pct,
          })
        })
      })

      // Upsert policy_advisors in batches
      if (policyAdvisorRecords.length > 0) {
        const ADVISOR_BATCH_SIZE = 25
        for (let i = 0; i < policyAdvisorRecords.length; i += ADVISOR_BATCH_SIZE) {
          const batch = policyAdvisorRecords.slice(i, i + ADVISOR_BATCH_SIZE)
          const { error } = await supabase
            .from('policy_advisors')
            .upsert(batch, { onConflict: 'policy_id,advisor_id' })
          if (error) throw error

          // Report progress: advisor splits
          const processedCount = Math.min(i + ADVISOR_BATCH_SIZE, policyAdvisorRecords.length)
          if (onProgress) onProgress({ current: processedCount, total: policyAdvisorRecords.length, phase: 'Linking advisors' })
        }
      }
    }

    return { imported: newCount, updated: updatedCount }
  }

  const deletePolicies = async (ids) => {
    const { error } = await supabase
      .from('policies')
      .delete()
      .in('id', ids)
    if (error) throw error
    await fetchPolicies()
  }

  return { policies, total, loading, error, createPolicy, updatePolicy, toggleActive, importPolicies, deletePolicies, refetch: fetchPolicies }
}
